import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, UserPlus } from "lucide-react";
import { FaLinkedinIn } from "react-icons/fa";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { ProfileAvatar } from "@/components/common/ProfileAvatar";
import { LinkedInProfileModal } from "@/components/candidates/LinkedInProfileModal";
import { AddCandidateForm } from "@/components/candidates/AddCandidateForm";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { getLinkedInProfile } from "@/hooks/inbox/api";
import { inboxKeys } from "@/hooks/inbox/queryKeys";
import { useConversationContext } from "@/hooks/inbox/useConversationContext";
import { useRefreshParticipantPicture } from "@/hooks/inbox/useRefreshParticipantPicture";
import type { CandidateFormData } from "@/utils/candidate";
import type { Candidate } from "@/types/candidate";
import { ChannelBadge } from "./ChannelBadge";
import { MatchedCandidateCard } from "./MatchedCandidateCard";
import { extractLinkedInId, getParticipantName } from "./utils";
import type { InboxConversation } from "@/types/inbox";

interface LinkedInProfileData {
	participant?: {
		first_name?: string;
		last_name?: string;
		public_identifier?: string;
		contact_info?: { emails?: string[]; phones?: string[] };
	};
}

/**
 * Right pane: who you're talking to, whether they're already in the CRM,
 * and the LinkedIn → candidate creation flow ported from the old drawer.
 */
export const ContextPanel: React.FC<{ conversation: InboxConversation }> = ({ conversation }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const {
		channel,
		primaryParticipant,
		participantDetails,
		participantsLoading,
		profileUrl,
		matchedCandidate,
		matchedCandidateLoading,
	} = useConversationContext(conversation);
	const refreshPicture = useRefreshParticipantPicture();

	const [linkedInModalOpen, setLinkedInModalOpen] = useState(false);
	const [linkedInProfileData, setLinkedInProfileData] = useState<LinkedInProfileData | null>(null);
	const [linkedInLoading, setLinkedInLoading] = useState(false);
	const [linkedInError, setLinkedInError] = useState("");
	const [addCandidateOpen, setAddCandidateOpen] = useState(false);
	const [candidateFormData, setCandidateFormData] = useState<CandidateFormData | null>(null);

	const displayName =
		getParticipantName(primaryParticipant) ||
		participantDetails?.name ||
		t("inbox.unknownSender", "Unknown");
	const email = primaryParticipant?.email || participantDetails?.email || "";
	const pictureUrl = participantDetails?.pictureUrl || primaryParticipant?.picture_url;

	const handleViewLinkedInProfile = async () => {
		const linkedinId = extractLinkedInId(profileUrl);
		if (!linkedinId) {
			toast({
				title: t("inbox.errors.linkedinTitle", "LinkedIn profile unavailable"),
				description: t("inbox.errors.linkedinDescription", "No valid LinkedIn profile URL found."),
				variant: "destructive",
			});
			return;
		}
		setLinkedInLoading(true);
		setLinkedInError("");
		setLinkedInModalOpen(true);
		try {
			const data = (await getLinkedInProfile(linkedinId)) as LinkedInProfileData;
			setLinkedInProfileData(data);
		} catch (error) {
			const message =
				error instanceof Error ? error.message : t("inbox.errors.linkedinTitle", "LinkedIn profile unavailable");
			setLinkedInError(message);
		} finally {
			setLinkedInLoading(false);
		}
	};

	// Turn the fetched LinkedIn profile into a pre-filled candidate form
	// (same quick-scan flow the old drawer used).
	const handleCreateCandidateFromLinkedIn = async () => {
		if (!linkedInProfileData) return;
		try {
			setLinkedInLoading(true);
			const { data: quickScanData, error } = await supabase.functions.invoke("quick-scan-candidate", {
				body: { cvText: JSON.stringify(linkedInProfileData) },
			});
			if (error) throw error;

			const participant = linkedInProfileData.participant;
			setCandidateFormData({
				firstName: participant?.first_name || quickScanData?.firstName || "",
				lastName: participant?.last_name || quickScanData?.lastName || "",
				email: participant?.contact_info?.emails?.[0] || quickScanData?.email || "",
				phone: participant?.contact_info?.phones?.[0] || quickScanData?.phone || "",
				role: quickScanData?.function || "",
				skills: Array.isArray(quickScanData?.skills)
					? quickScanData.skills.join(", ")
					: quickScanData?.skills || "",
				summary: quickScanData?.summary || "",
				linkedin: participant?.public_identifier
					? `https://www.linkedin.com/in/${participant.public_identifier}`
					: "",
				cvFile: null,
				linkedinJson: JSON.stringify(linkedInProfileData),
			});
			setLinkedInModalOpen(false);
			setAddCandidateOpen(true);
		} catch (error) {
			console.error("Error processing LinkedIn data for candidate creation:", error);
			toast({
				title: t("inbox.errors.linkedinProcessTitle", "Couldn't process LinkedIn data"),
				description: error instanceof Error ? error.message : undefined,
				variant: "destructive",
			});
		} finally {
			setLinkedInLoading(false);
		}
	};

	const handleAddAsCandidate = () => {
		const [firstName, ...rest] = displayName.split(" ");
		setCandidateFormData({
			firstName: firstName || "",
			lastName: rest.join(" "),
			email,
			phone: "",
			role: "",
			skills: "",
			summary: "",
			linkedin: profileUrl || "",
			cvFile: null,
		} as CandidateFormData);
		setAddCandidateOpen(true);
	};

	const handleCandidateAdded = async (candidate: Candidate) => {
		setAddCandidateOpen(false);
		setCandidateFormData(null);
		queryClient.invalidateQueries({ queryKey: [...inboxKeys.all, "candidate"] });

		// Queue AI enrichment for the new candidate, as the old flow did.
		try {
			const { error } = await supabase.functions.invoke("ai-processing?command=push-new-analysis", {
				body: {
					entityId: candidate.id,
					entityType: "candidate",
					analysisType: "analyse-resume",
					actionOnComplete: "update-candidate",
				},
			});
			if (error) console.error("Error triggering AI analysis:", error);
		} catch (error) {
			console.error("Error triggering AI processing:", error);
		}

		toast({
			title: t("inbox.context.candidateCreatedTitle", "Candidate created"),
			description: t("inbox.context.candidateCreatedDescription", "{{name}} has been added successfully.", {
				name: candidate.name,
			}),
		});
		navigate(`/candidates/${candidate.id}`);
	};

	return (
		<div className="flex h-full min-h-0 flex-col overflow-y-auto scrollbar-thin bg-background">
			<div className="flex flex-col items-center gap-2 p-5 text-center">
				<ProfileAvatar
					src={pictureUrl}
					alt={displayName}
					className="h-16 w-16"
					participantId={primaryParticipant?.id}
					conversationId={conversation.id}
					onImageError={refreshPicture}
				/>
				{participantsLoading && !displayName ? (
					<Skeleton className="h-4 w-28" />
				) : (
					<p className="max-w-full truncate text-sm font-semibold text-foreground">{displayName}</p>
				)}
				{email && <p className="max-w-full truncate text-xs text-muted-foreground">{email}</p>}
				<ChannelBadge channel={channel} />
			</div>

			<Separator />

			<div className="space-y-3 p-4">
				{matchedCandidateLoading ? (
					<Skeleton className="h-24 w-full rounded-sm" />
				) : matchedCandidate ? (
					<MatchedCandidateCard candidate={matchedCandidate} />
				) : (
					<Button variant="outline" size="sm" className="w-full gap-2" onClick={handleAddAsCandidate}>
						<UserPlus className="h-4 w-4" />
						{t("inbox.context.addAsCandidate", "Add as candidate")}
					</Button>
				)}

				{channel === "linkedin" && profileUrl && (
					<Button
						variant="outline"
						size="sm"
						className="w-full gap-2"
						onClick={handleViewLinkedInProfile}
						disabled={linkedInLoading}
					>
						{linkedInLoading ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : (
							<FaLinkedinIn className="h-3.5 w-3.5" />
						)}
						{t("inbox.context.viewLinkedIn", "View LinkedIn profile")}
					</Button>
				)}
			</div>

			<Separator />

			<div className="space-y-2 p-4 text-xs text-muted-foreground">
				{conversation.subject && (
					<div>
						<p className="font-medium uppercase tracking-wide">{t("inbox.context.subject", "Subject")}</p>
						<p className="mt-0.5 break-words text-foreground">{conversation.subject}</p>
					</div>
				)}
				{conversation.source_account_type && (
					<div>
						<p className="font-medium uppercase tracking-wide">{t("inbox.context.account", "Account")}</p>
						<p className="mt-0.5 text-foreground">{conversation.source_account_type}</p>
					</div>
				)}
			</div>

			<LinkedInProfileModal
				isOpen={linkedInModalOpen}
				onClose={() => {
					setLinkedInModalOpen(false);
					setLinkedInProfileData(null);
					setLinkedInError("");
				}}
				profileData={linkedInProfileData}
				loading={linkedInLoading}
				error={linkedInError}
				onUpdateCandidate={
					matchedCandidate
						? () => {
								setLinkedInModalOpen(false);
								navigate(`/candidates/${matchedCandidate.id}`);
							}
						: handleCreateCandidateFromLinkedIn
				}
				showUpdateButton={!linkedInLoading && !linkedInError && !!linkedInProfileData}
				candidateExists={!!matchedCandidate}
			/>

			{addCandidateOpen &&
				createPortal(
					<AddCandidateForm
						isOpen={addCandidateOpen}
						onClose={() => {
							setAddCandidateOpen(false);
							setCandidateFormData(null);
						}}
						onCandidateAdded={handleCandidateAdded}
						initialFormData={candidateFormData}
					/>,
					document.body
				)}
		</div>
	);
};
